/**
 * Allocation free signature utilities.
 *
 * Useful to detect in place mutations of data that a component derives its display state from:
 * such mutations do not change the object reference, so Angular inputs do not fire. Comparing a
 * cheap signature on every change detection pass detects them without rebuilding the derived state
 * each time.
 *
 * Signatures are 32 bit hashes: they are meant for change detection, not for identity. A collision
 * only means a missed refresh, so include every field the derived state depends on.
 *
 * @example
 * private computeSignature(node: TreeNode<Task>): number {
 *     let signature = SignatureUtils.START;
 *     signature = SignatureUtils.mixToken(signature, node.data?.status);
 *     signature = SignatureUtils.mixNumber(signature, node.data?.attributes1);
 *     return SignatureUtils.mixTokens(signature, node.children, (x) => x.data?.status);
 * }
 */
export class SignatureUtils {
    /**
     * Starting value of a signature
     */
    public static readonly START = 7;

    private static readonly _MIXER = 31;
    private static readonly _tokenIds = new Map<string, number>();

    // ===============================================================
    // ===============================================================

    /**
     * Mix a number into a signature
     * @param signature Current signature
     * @param value Value to mix in (`undefined` and `null` are mixed as `0`)
     * @returns New signature
     */
    public static mixNumber(signature: number, value: number | undefined | null): number {
        return (signature * SignatureUtils._MIXER + (value ?? 0)) | 0;
    }
    /**
     * Mix a boolean into a signature
     * @param signature Current signature
     * @param value Value to mix in
     * @returns New signature
     */
    public static mixBoolean(signature: number, value: boolean | undefined | null): number {
        return SignatureUtils.mixNumber(signature, value ? 1 : 0);
    }
    /**
     * Mix a low cardinality string (enum value, UUID, ...) into a signature.
     *
     * Each distinct string is assigned a stable numeric id on first use, so mixing it needs no
     * allocation. Do not use for unbounded values (free text, timestamps, ...) as every distinct
     * value is retained: use {@link mixString} instead
     * @param signature Current signature
     * @param token Token to mix in (`undefined` and `null` are mixed as `0`)
     * @returns New signature
     */
    public static mixToken(signature: number, token: string | undefined | null): number {
        return SignatureUtils.mixNumber(signature, token ? SignatureUtils.tokenId(token) : 0);
    }
    /**
     * Mix an arbitrary string into a signature, hashing it character by character
     * @param signature Current signature
     * @param value Value to mix in (`undefined` and `null` are mixed as `0`)
     * @returns New signature
     */
    public static mixString(signature: number, value: string | undefined | null): number {
        if (!value) return SignatureUtils.mixNumber(signature, 0);

        let result = SignatureUtils.mixNumber(signature, value.length);
        for (let i = 0; i < value.length; ++i) result = (result * SignatureUtils._MIXER + value.charCodeAt(i)) | 0;

        return result;
    }
    /**
     * Mix the tokens of a collection into a signature, including its length
     * @param signature Current signature
     * @param items Items to mix in
     * @param fnGetToken Get the token of an item
     * @returns New signature
     */
    public static mixTokens<t_Type>(
        signature: number,
        items: readonly t_Type[] | undefined | null,
        fnGetToken: (item: t_Type) => string | undefined | null
    ): number {
        const count = items?.length ?? 0;

        let result = SignatureUtils.mixNumber(signature, count);
        for (let i = 0; i < count; ++i) result = SignatureUtils.mixToken(result, fnGetToken(items![i]));

        return result;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Stable numeric id of a token
     * @param token Token
     * @returns Numeric id
     */
    private static tokenId(token: string): number {
        let id = SignatureUtils._tokenIds.get(token);
        if (id === undefined) {
            id = SignatureUtils._tokenIds.size + 1;
            SignatureUtils._tokenIds.set(token, id);
        }

        return id;
    }
}
